// OBD-II (SAE J1979 / ISO 15031-5) decoding: Mode 01 PIDs, Mode 03/07/0A DTC lists,
// Mode 09 VIN. Every Mode 01 formula below was checked against the Wikipedia
// "OBD-II PIDs" article (Service 01 table and the bit-encoded PID sections), which
// summarises SAE J1979; readiness-bit polarity (1 = NOT complete) follows J1979.
import { hex2 } from './constants';
import { dtcBytesToCode } from './dtc';

// --- unit handling -----------------------------------------------------------

const UNIT_CONVERSIONS = {
  '°C': { imperial: '°F', to: (v) => (v * 9) / 5 + 32, from: (v) => ((v - 32) * 5) / 9 },
  'km/h': { imperial: 'mph', to: (v) => v / 1.609344, from: (v) => v * 1.609344 },
  kPa: { imperial: 'psi', to: (v) => v * 0.1450377, from: (v) => v / 0.1450377 },
  'g/s': { imperial: 'lb/min', to: (v) => v * 0.1322774, from: (v) => v / 0.1322774 },
  'L/h': { imperial: 'gal/h (US)', to: (v) => v / 3.785411784, from: (v) => v * 3.785411784 },
  km: { imperial: 'mi', to: (v) => v / 1.609344, from: (v) => v * 1.609344 },
};

export const displayUnit = (unit, units) => (units === 'imperial' && UNIT_CONVERSIONS[unit] ? UNIT_CONVERSIONS[unit].imperial : unit);
export const toDisplay = (value, unit, units) => (units === 'imperial' && UNIT_CONVERSIONS[unit] ? UNIT_CONVERSIONS[unit].to(value) : value);
export const fromDisplay = (value, unit, units) => (units === 'imperial' && UNIT_CONVERSIONS[unit] ? UNIT_CONVERSIONS[unit].from(value) : value);

const fmt = (v, dp = 2) => String(Number(v.toFixed(dp)));

// --- enumerations (Wikipedia "OBD-II PIDs", PID 1C and PID 51 tables) ---------

export const OBD_STANDARDS = {
  1: 'OBD-II as defined by CARB', 2: 'OBD as defined by the EPA', 3: 'OBD and OBD-II', 4: 'OBD-I', 5: 'Not OBD compliant',
  6: 'EOBD (Europe)', 7: 'EOBD and OBD-II', 8: 'EOBD and OBD', 9: 'EOBD, OBD and OBD-II', 10: 'JOBD (Japan)',
  11: 'JOBD and OBD-II', 12: 'JOBD and EOBD', 13: 'JOBD, EOBD and OBD-II', 14: 'OBD, EOBD and KOBD', 15: 'OBD, OBD-II, EOBD and KOBD',
  16: 'Reserved', 17: 'Engine Manufacturer Diagnostics (EMD)', 18: 'Engine Manufacturer Diagnostics Enhanced (EMD+)',
  19: 'Heavy Duty OBD (Child/Partial) (HD OBD-C)', 20: 'Heavy Duty OBD (HD OBD)', 21: 'World Wide Harmonized OBD (WWH OBD)', 22: 'Reserved',
  23: 'Heavy Duty Euro OBD Stage I without NOx control', 24: 'Heavy Duty Euro OBD Stage I with NOx control',
  25: 'Heavy Duty Euro OBD Stage II without NOx control', 26: 'Heavy Duty Euro OBD Stage II with NOx control', 27: 'Heavy Duty ZEV',
  28: 'Brazil OBD Phase 1 (OBDBr-1)', 29: 'Brazil OBD Phase 2 (OBDBr-2)', 30: 'Korean OBD (KOBD)', 31: 'India OBD I (IOBD I)',
  32: 'India OBD II (IOBD II)', 33: 'Heavy Duty Euro OBD Stage VI', 34: 'OBD, OBD-II and HD OBD', 35: 'Brazil OBD Phase 3 (OBDBr-3)',
};

export const FUEL_TYPES = {
  0: 'Not available', 1: 'Gasoline', 2: 'Methanol', 3: 'Ethanol', 4: 'Diesel', 5: 'LPG', 6: 'CNG', 7: 'Propane', 8: 'Electric',
  9: 'Bifuel running gasoline', 10: 'Bifuel running methanol', 11: 'Bifuel running ethanol', 12: 'Bifuel running LPG',
  13: 'Bifuel running CNG', 14: 'Bifuel running propane', 15: 'Bifuel running electricity', 16: 'Bifuel running electric and combustion engine',
  17: 'Hybrid gasoline', 18: 'Hybrid ethanol', 19: 'Hybrid diesel', 20: 'Hybrid electric', 21: 'Hybrid running electric and combustion engine',
  22: 'Hybrid regenerative', 23: 'Bifuel running diesel',
};

const enumName = (table, v, what) => {
  if (table[v]) return table[v];
  if (what === 'standard' && v >= 251) return 'Not available for assignment (SAE J1939 special meaning)';
  if (what === 'standard' && v >= 36 && v <= 52) return 'Not listed in the public source used by this tool';
  return 'Reserved by ISO/SAE';
};

// --- Mode 01 PID definitions ---------------------------------------------------
// kind: 'value' (formula) | 'bitmap' | 'monitor' | 'enum'.
// `formula` uses A/B; `calc` returns the metric value; `encode` returns data bytes
// from a metric value (null if out of range).

const linear1 = (calc, encode) => ({ bytes: 1, calc: ([a]) => calc(a), encode });
const word = (div) => ({ bytes: 2, calc: ([a, b]) => (256 * a + b) / div, encode: (v) => { const n = Math.round(v * div); return n < 0 || n > 0xffff ? null : [n >> 8, n & 0xff]; } });
const byteIn = (n) => (n < 0 || n > 255 ? null : [n]);

const PID_LIST = [
  ...[0x00, 0x20, 0x40, 0x60, 0x80, 0xa0, 0xc0].map((pid) => ({ pid, name: `PIDs supported [${hex2(pid + 1)}–${hex2(pid + 0x20)}]`, kind: 'bitmap', bytes: 4 })),
  { pid: 0x01, name: 'Monitor status since DTCs cleared', kind: 'monitor', bytes: 4 },
  { pid: 0x04, name: 'Calculated engine load', kind: 'value', unit: '%', formula: 'A×100/255', ...linear1((a) => (a * 100) / 255, (v) => byteIn(Math.round((v * 255) / 100))), min: 0, max: 100 },
  { pid: 0x05, name: 'Engine coolant temperature', kind: 'value', unit: '°C', formula: 'A − 40', ...linear1((a) => a - 40, (v) => byteIn(Math.round(v + 40))), min: -40, max: 215 },
  ...[[0x06, 'Short-term fuel trim — bank 1'], [0x07, 'Long-term fuel trim — bank 1'], [0x08, 'Short-term fuel trim — bank 2'], [0x09, 'Long-term fuel trim — bank 2']].map(([pid, name]) => ({
    pid, name, kind: 'value', unit: '%', formula: '(A − 128)×100/128', ...linear1((a) => ((a - 128) * 100) / 128, (v) => byteIn(Math.round((v * 128) / 100 + 128))), min: -100, max: 99.2,
  })),
  { pid: 0x0a, name: 'Fuel pressure (gauge)', kind: 'value', unit: 'kPa', formula: '3×A', ...linear1((a) => 3 * a, (v) => byteIn(Math.round(v / 3))), min: 0, max: 765 },
  { pid: 0x0b, name: 'Intake manifold absolute pressure (MAP)', kind: 'value', unit: 'kPa', formula: 'A', ...linear1((a) => a, (v) => byteIn(Math.round(v))), min: 0, max: 255 },
  { pid: 0x0c, name: 'Engine speed', kind: 'value', unit: 'rpm', formula: '(256×A + B)/4', ...word(4), min: 0, max: 16383.75 },
  { pid: 0x0d, name: 'Vehicle speed', kind: 'value', unit: 'km/h', formula: 'A', ...linear1((a) => a, (v) => byteIn(Math.round(v))), min: 0, max: 255 },
  { pid: 0x0e, name: 'Timing advance', kind: 'value', unit: '° before TDC', formula: 'A/2 − 64', ...linear1((a) => a / 2 - 64, (v) => byteIn(Math.round((v + 64) * 2))), min: -64, max: 63.5 },
  { pid: 0x0f, name: 'Intake air temperature', kind: 'value', unit: '°C', formula: 'A − 40', ...linear1((a) => a - 40, (v) => byteIn(Math.round(v + 40))), min: -40, max: 215 },
  { pid: 0x10, name: 'Mass air flow (MAF) rate', kind: 'value', unit: 'g/s', formula: '(256×A + B)/100', ...word(100), min: 0, max: 655.35 },
  { pid: 0x11, name: 'Throttle position', kind: 'value', unit: '%', formula: 'A×100/255', ...linear1((a) => (a * 100) / 255, (v) => byteIn(Math.round((v * 255) / 100))), min: 0, max: 100 },
  { pid: 0x1c, name: 'OBD standard this vehicle conforms to', kind: 'enum', bytes: 1, table: 'standard' },
  { pid: 0x1f, name: 'Run time since engine start', kind: 'value', unit: 's', formula: '256×A + B', ...word(1), min: 0, max: 65535 },
  { pid: 0x21, name: 'Distance travelled with MIL on', kind: 'value', unit: 'km', formula: '256×A + B', ...word(1), min: 0, max: 65535 },
  { pid: 0x2f, name: 'Fuel tank level input', kind: 'value', unit: '%', formula: 'A×100/255', ...linear1((a) => (a * 100) / 255, (v) => byteIn(Math.round((v * 255) / 100))), min: 0, max: 100 },
  { pid: 0x31, name: 'Distance travelled since codes cleared', kind: 'value', unit: 'km', formula: '256×A + B', ...word(1), min: 0, max: 65535 },
  { pid: 0x33, name: 'Absolute barometric pressure', kind: 'value', unit: 'kPa', formula: 'A', ...linear1((a) => a, (v) => byteIn(Math.round(v))), min: 0, max: 255 },
  { pid: 0x42, name: 'Control module voltage', kind: 'value', unit: 'V', formula: '(256×A + B)/1000', ...word(1000), min: 0, max: 65.535 },
  { pid: 0x46, name: 'Ambient air temperature', kind: 'value', unit: '°C', formula: 'A − 40', ...linear1((a) => a - 40, (v) => byteIn(Math.round(v + 40))), min: -40, max: 215 },
  { pid: 0x51, name: 'Fuel type', kind: 'enum', bytes: 1, table: 'fuel' },
  { pid: 0x5c, name: 'Engine oil temperature', kind: 'value', unit: '°C', formula: 'A − 40', ...linear1((a) => a - 40, (v) => byteIn(Math.round(v + 40))), min: -40, max: 210 },
  { pid: 0x5e, name: 'Engine fuel rate', kind: 'value', unit: 'L/h', formula: '(256×A + B)/20', ...word(20), min: 0, max: 3212.75 },
];

export const MODE01_PIDS = Object.fromEntries(PID_LIST.map((p) => [p.pid, p]));
export const MODE01_PID_ROWS = PID_LIST.map((p) => ({
  ...p,
  hex: hex2(p.pid),
  formulaText: p.kind === 'value' ? `${p.formula} ${p.unit}` : p.kind === 'bitmap' ? 'Bitmap: A7…D0 → next 32 PIDs supported' : p.kind === 'monitor' ? 'A7 = MIL, A6–A0 = DTC count, B/C/D = readiness monitors' : 'Enumerated (lookup table)',
  searchText: `${hex2(p.pid)} ${p.name} ${p.formula || ''} ${p.unit || ''}`.toLowerCase(),
}));
export const ENCODABLE_PIDS = PID_LIST.filter((p) => p.kind === 'value');

// --- PID 01 readiness monitors (Wikipedia "OBD-II PIDs", Service 01 PID 01) -----

const COMMON_MONITORS = [
  { name: 'Components', avail: 2, incomplete: 6 },
  { name: 'Fuel system', avail: 1, incomplete: 5 },
  { name: 'Misfire', avail: 0, incomplete: 4 },
];
const SPARK_MONITORS = ['Catalyst', 'Heated catalyst', 'Evaporative system', 'Secondary air system', 'Gasoline particulate filter', 'Oxygen sensor', 'Oxygen sensor heater', 'EGR and/or VVT system'];
const COMPRESSION_MONITORS = ['NMHC catalyst', 'NOx/SCR monitor', 'Reserved', 'Boost pressure', 'Reserved', 'Exhaust gas sensor', 'PM filter monitoring', 'EGR and/or VVT system'];

export function decodeMonitorStatus([a, b, c, d]) {
  const mil = (a & 0x80) !== 0;
  const dtcCount = a & 0x7f;
  const compression = (b & 0x08) !== 0;
  const monitors = COMMON_MONITORS.map((m) => ({
    name: m.name,
    group: 'Common',
    available: (b & (1 << m.avail)) !== 0,
    complete: (b & (1 << m.incomplete)) === 0,
  }));
  const names = compression ? COMPRESSION_MONITORS : SPARK_MONITORS;
  names.forEach((name, bit) => {
    if (name === 'Reserved') return;
    monitors.push({ name, group: compression ? 'Compression ignition' : 'Spark ignition', available: (c & (1 << bit)) !== 0, complete: (d & (1 << bit)) === 0 });
  });
  return { mil, dtcCount, engineType: compression ? 'Compression ignition (diesel)' : 'Spark ignition (petrol/gasoline)', monitors };
}

// --- single PID decode ----------------------------------------------------------

export function decodePidValue(pidDef, data, units = 'metric') {
  const [a, b] = data;
  const base = { pid: pidDef.pid, hex: hex2(pidDef.pid), name: pidDef.name, kind: pidDef.kind, data };
  if (pidDef.kind === 'bitmap') {
    const supported = [];
    for (let i = 0; i < 32; i += 1) {
      const byte = data[Math.floor(i / 8)];
      if (byte & (0x80 >> (i % 8))) supported.push(pidDef.pid + i + 1);
    }
    const next = supported.includes(pidDef.pid + 0x20);
    return { ...base, supported, display: supported.length ? supported.map(hex2).join(', ') : 'none', note: next ? `PID ${hex2(pidDef.pid + 0x20)} is set, so the next "PIDs supported" block can be requested.` : 'The last bit is clear: no further "PIDs supported" block is available.' };
  }
  if (pidDef.kind === 'monitor') {
    const m = decodeMonitorStatus(data);
    return { ...base, monitor: m, display: `MIL ${m.mil ? 'ON' : 'off'}, ${m.dtcCount} emissions DTC${m.dtcCount === 1 ? '' : 's'}` };
  }
  if (pidDef.kind === 'enum') {
    const table = pidDef.table === 'fuel' ? FUEL_TYPES : OBD_STANDARDS;
    return { ...base, display: `${a} — ${enumName(table, a, pidDef.table)}` };
  }
  const metric = pidDef.calc(data);
  const shown = toDisplay(metric, pidDef.unit, units);
  const unit = displayUnit(pidDef.unit, units);
  // Single-pass substitution: a hex value such as 0xBB must not be re-substituted.
  const sub = pidDef.formula.replace(/[AB]/g, (ch) => `0x${hex2(ch === 'A' ? a : b ?? 0)}`);
  const subDec = pidDef.formula.replace(/[AB]/g, (ch) => String(ch === 'A' ? a : b ?? 0));
  const converted = unit !== pidDef.unit ? ` → ${fmt(shown)} ${unit}` : '';
  return {
    ...base,
    value: shown,
    metricValue: metric,
    unit,
    display: `${fmt(shown)} ${unit}`,
    formulaLine: `${pidDef.formula} = ${sub} = ${subDec} = ${fmt(metric)} ${pidDef.unit}${converted}`,
  };
}

export function encodePidValue(pid, displayValue, units = 'metric') {
  const def = MODE01_PIDS[pid];
  if (!def || def.kind !== 'value') return { error: 'This PID cannot be encoded from a single value.' };
  const metric = fromDisplay(displayValue, def.unit, units);
  if (!Number.isFinite(metric)) return { error: 'Enter a number.' };
  if (metric < def.min - 1e-9 || metric > def.max + 1e-9) {
    const lo = fmt(toDisplay(def.min, def.unit, units));
    const hi = fmt(toDisplay(def.max, def.unit, units));
    return { error: `Out of range for PID ${hex2(pid)}: ${lo} to ${hi} ${displayUnit(def.unit, units)}.` };
  }
  const data = def.encode(metric);
  if (!data) return { error: 'Value cannot be represented in this PID\'s bytes.' };
  const roundTrip = decodePidValue(def, data, units);
  return { data, response: [0x41, pid, ...data], request: [0x01, pid], roundTrip };
}

// --- Mode 01 response (can hold several PIDs on CAN) ----------------------------

export function decodeMode01Response(bytes, units) {
  const items = [];
  const warnings = [];
  let i = 1;
  while (i < bytes.length) {
    const pid = bytes[i];
    const def = MODE01_PIDS[pid];
    if (!def) {
      warnings.push(`PID ${hex2(pid)} is not in this tool's PID table, so its data length is unknown. Remaining bytes shown raw: ${bytes.slice(i + 1).map(hex2).join(' ') || '(none)'}.`);
      items.push({ pid, hex: hex2(pid), name: 'Unsupported PID', kind: 'raw', data: bytes.slice(i + 1), display: bytes.slice(i + 1).map(hex2).join(' ') || '—' });
      break;
    }
    const data = bytes.slice(i + 1, i + 1 + def.bytes);
    if (data.length < def.bytes) {
      warnings.push(`PID ${hex2(pid)} (${def.name}) needs ${def.bytes} data byte${def.bytes > 1 ? 's' : ''} but only ${data.length} ${data.length === 1 ? 'was' : 'were'} given — response truncated.`);
      break;
    }
    items.push(decodePidValue(def, data, units));
    i += 1 + def.bytes;
  }
  return { items, warnings };
}

// --- Mode 03 / 07 / 0A DTC lists -------------------------------------------------

export const DTC_MODES = {
  0x43: { mode: 0x03, name: 'Stored (confirmed) emissions DTCs' },
  0x47: { mode: 0x07, name: 'Pending emissions DTCs (current/last drive cycle)' },
  0x4a: { mode: 0x0a, name: 'Permanent emissions DTCs (cannot be cleared with Mode 04)' },
};

// format: 'auto' | 'can' | 'legacy'. CAN (ISO 15765-4) has a DTC-count byte after the
// SID; older protocols do not and pad each frame to 3 DTCs with 00 00.
export function decodeObdDtcList(bytes, format = 'auto') {
  const rest = bytes.slice(1);
  const warnings = [];
  let detected = format;
  if (format === 'auto') {
    if (rest.length === 0) detected = 'legacy';
    else if (rest.length % 2 === 1) detected = 'can';
    else detected = 'legacy';
  }
  let pairs;
  let count = null;
  let formatReason;
  if (detected === 'can') {
    count = rest[0];
    const body = rest.slice(1);
    formatReason = format === 'auto' ? 'An odd number of bytes follow the SID, which fits the CAN form: 1 count byte + 2 bytes per DTC.' : 'CAN form selected: 1 count byte + 2 bytes per DTC.';
    if (body.length % 2 === 1) warnings.push('Odd number of DTC bytes after the count byte — the last byte is incomplete and was ignored.');
    const complete = Math.floor(body.length / 2);
    if (count !== undefined && count !== complete) warnings.push(`The count byte says ${count} DTC${count === 1 ? '' : 's'} but ${complete} complete DTC${complete === 1 ? ' is' : 's are'} present. ${count > complete ? 'The response looks truncated (a multi-frame response may be incomplete); the complete ones are decoded.' : 'Extra pairs were decoded anyway.'}`);
    pairs = [];
    for (let i = 0; i + 1 < body.length; i += 2) pairs.push([body[i], body[i + 1]]);
  } else {
    formatReason = format === 'auto' ? 'An even number of bytes follow the SID, which fits the pre-CAN form (no count byte; 00 00 pairs are padding).' : 'Legacy (non-CAN) form selected: no count byte; 00 00 pairs are padding.';
    if (rest.length % 2 === 1) warnings.push('Odd number of bytes — the last byte is incomplete and was ignored.');
    pairs = [];
    for (let i = 0; i + 1 < rest.length; i += 2) pairs.push([rest[i], rest[i + 1]]);
    const before = pairs.length;
    pairs = pairs.filter(([x, y]) => x !== 0 || y !== 0);
    if (before !== pairs.length) warnings.push(`${before - pairs.length} padding pair${before - pairs.length === 1 ? '' : 's'} (00 00) skipped.`);
  }
  return {
    format: detected,
    formatReason,
    count,
    dtcs: pairs.map((p) => ({ bytes: p, code: dtcBytesToCode(p) })),
    warnings,
  };
}

// --- Mode 09 VIN ------------------------------------------------------------------

const VIN_VALUES = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8, J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9, S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9 };
const VIN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

export function validateVin(vin) {
  const issues = [];
  if (vin.length !== 17) issues.push(`A VIN has 17 characters; this one has ${vin.length}.`);
  const bad = [...new Set(vin.split('').filter((ch) => /[IOQ]/.test(ch)))];
  if (bad.length) issues.push(`Contains ${bad.join(', ')} — the letters I, O and Q are never used in a VIN (they look like 1 and 0).`);
  const invalid = [...new Set(vin.split('').filter((ch) => !/[A-Z0-9]/.test(ch)))];
  if (invalid.length) issues.push(`Contains characters that are not allowed in a VIN: ${invalid.map((c) => JSON.stringify(c)).join(', ')}.`);
  let checkDigit = null;
  if (!issues.length) {
    const sum = vin.split('').reduce((acc, ch, i) => acc + (/[0-9]/.test(ch) ? Number(ch) : VIN_VALUES[ch]) * VIN_WEIGHTS[i], 0);
    const expected = sum % 11 === 10 ? 'X' : String(sum % 11);
    checkDigit = { expected, actual: vin[8], valid: expected === vin[8] };
  }
  return { valid: issues.length === 0, issues, checkDigit };
}

export function decodeMode09(bytes) {
  const pid = bytes[1];
  if (pid === undefined) return { error: 'Mode 09 response has no PID byte.' };
  if (pid === 0x00) {
    if (bytes.length < 6) return { error: 'Mode 09 PID 00 needs 4 bitmap bytes.' };
    const v = decodePidValue({ pid: 0x00, name: 'Mode 09 PIDs supported [01–20]', kind: 'bitmap' }, bytes.slice(2, 6));
    return { pid, name: v.name, display: v.display, supported: v.supported };
  }
  if (pid !== 0x02) return { pid, name: `Mode 09 PID ${hex2(pid)}`, unsupported: true, raw: bytes.slice(2) };
  let data = bytes.slice(2);
  let nodi = null;
  if (data.length === 18) {
    nodi = data[0];
    data = data.slice(1);
  }
  const notes = [];
  if (nodi !== null) notes.push(`First data byte 0x${hex2(nodi)} is the number of data items (CAN form), not part of the VIN.`);
  const leadingPad = data.findIndex((x) => x !== 0x00);
  if (leadingPad > 0) {
    notes.push(`${leadingPad} leading 0x00 padding byte${leadingPad === 1 ? '' : 's'} removed.`);
    data = data.slice(leadingPad);
  }
  const nonAscii = data.filter((x) => x < 0x20 || x > 0x7e);
  if (nonAscii.length) return { pid, error: `VIN bytes include non-printable values (${nonAscii.map(hex2).join(' ')}), so this is not an ASCII VIN.` };
  const vin = String.fromCharCode(...data);
  return { pid, name: 'Vehicle Identification Number (VIN)', vin, notes, validation: validateVin(vin) };
}

// --- top-level OBD message decode (used by the OBD-II tab) ------------------------

const REQUEST_MODES = {
  0x01: 'Mode 01 — show current data',
  0x03: 'Mode 03 — request stored (confirmed) DTCs',
  0x04: 'Mode 04 — clear DTCs and stored values (turns the MIL off)',
  0x07: 'Mode 07 — request pending DTCs',
  0x09: 'Mode 09 — request vehicle information',
  0x0a: 'Mode 0A — request permanent DTCs',
};

export function decodeObdMessage(bytes, { units = 'metric', dtcFormat = 'auto' } = {}) {
  const sid = bytes[0];
  if (sid === 0x41) {
    if (bytes.length < 2) return { ok: false, error: '41 alone is incomplete — a Mode 01 response continues with the PID and its data bytes (e.g. 41 0D 3C).' };
    const r = decodeMode01Response(bytes, units);
    if (!r.items.length) return { ok: false, error: r.warnings[0] || 'No PID could be decoded.' };
    return { ok: true, type: 'mode01', title: 'Mode 01 response — current data', ...r };
  }
  if (DTC_MODES[sid]) {
    const r = decodeObdDtcList(bytes, dtcFormat);
    return { ok: true, type: 'dtcList', title: `Mode ${hex2(DTC_MODES[sid].mode)} response — ${DTC_MODES[sid].name}`, ...r };
  }
  if (sid === 0x44) return { ok: true, type: 'info', title: 'Mode 04 positive response', text: 'The ECU cleared its emissions DTCs, freeze-frame data and readiness monitor results. Readiness monitors now read "not complete" until they run again.', warnings: [] };
  if (sid === 0x49) {
    const r = decodeMode09(bytes);
    if (r.error) return { ok: false, error: r.error };
    return { ok: true, type: 'mode09', title: `Mode 09 response — ${r.name}`, ...r, warnings: [] };
  }
  if (REQUEST_MODES[sid]) {
    const lines = [REQUEST_MODES[sid]];
    if (sid === 0x01) {
      bytes.slice(1).forEach((pid) => lines.push(`PID ${hex2(pid)} — ${MODE01_PIDS[pid] ? MODE01_PIDS[pid].name : 'not in this tool\'s PID table'}`));
      if (bytes.length > 2) lines.push('Several PIDs in one request is allowed on CAN (up to 6).');
    }
    if (sid === 0x09 && bytes[1] !== undefined) lines.push(`PID ${hex2(bytes[1])}${bytes[1] === 0x02 ? ' — Vehicle Identification Number (VIN)' : bytes[1] === 0x00 ? ' — supported Mode 09 PIDs' : ''}`);
    const expect = sid === 0x01 ? `The ECU answers 41 ${bytes.slice(1).map(hex2).join(' ')} … with the data.` : sid === 0x09 ? 'The ECU answers 49 …' : `The ECU answers ${hex2(sid + 0x40)} …`;
    return { ok: true, type: 'request', title: 'OBD-II request', lines, text: expect, warnings: [] };
  }
  return { ok: false, error: `0x${hex2(sid)} is not an OBD-II service this tool decodes (41, 43, 44, 47, 49, 4A responses; 01, 03, 04, 07, 09, 0A requests).` };
}
